import type { BuilderNode, BuilderNodeKind, BuilderTemplate, BuilderTemplateId } from '../types/builder'

const node = (label: string, kind: BuilderNodeKind, detail: string, settings: Record<string, string>): BuilderNode => ({
  id: label.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
  label,
  kind,
  detail,
  settings: Object.entries(settings).map(([key, value]) => ({ key, value })),
})

/**
 * Scripted starter templates for the DGPT Builder preview. Companies, figures and
 * statuses are fictional. Nothing here is connected to live models or data.
 */
export const BUILDER_TEMPLATES: BuilderTemplate[] = [
  {
    id: 'procurement',
    name: 'Procurement Due Diligence',
    prompt:
      'Build a procurement due diligence application for our company. We need supplier onboarding, document verification, financial analysis, risk scoring, approval workflows, and ongoing monitoring.',
    reply: 'Here’s a plan for your procurement due diligence application. I’ll build the workflow first, then a vendor dashboard your team can refine.',
    plan: [
      'Supplier onboarding form and document collection',
      'Document verification and classification',
      'Financial analysis and benchmarking against your thresholds',
      'Risk scoring with an escalation rule',
      'Procurement approval and an approval report',
      'Quarterly vendor monitoring',
    ],
    nodes: [
      node('Intake', 'intake', 'Supplier onboarding form', { Channel: 'Supplier portal form', 'Required documents': 'W-9, insurance certificate, security questionnaire', Owner: 'Procurement ops' }),
      node('Extract', 'agent', 'Document Extraction Agent', { Agent: 'Document Extraction', Output: 'Structured supplier record', Review: 'Spot check 10%' }),
      node('Classify', 'agent', 'Classification Agent', { Agent: 'Industry Classification', Taxonomy: 'Your supplier categories', Output: 'Category and criticality tier' }),
      node('Enrich', 'data', 'External company data', { Source: 'Company registry and financial data feeds', Status: 'Connection configured at deployment', Refresh: 'On intake' }),
      node('Analyze', 'agent', 'Financial Analysis Agent', { Agent: 'Financial Analysis', Inputs: 'Statements, credit data', Output: 'Financial health summary with sources' }),
      node('Benchmark', 'agent', 'Benchmarking Agent', { Agent: 'Benchmarking', Compare: 'Suppliers in the same category', Output: 'Percentile vs peers' }),
      node('Risk threshold', 'condition', 'If risk score ≥ 70', { Rule: 'Risk score ≥ 70', 'If true': 'Escalate to risk committee', Otherwise: 'Continue to approval', Type: 'Deterministic rule' }),
      node('Approve', 'human', 'Procurement approval', { Approver: 'Head of Procurement', 'Second approver': 'Required for critical suppliers', SLA: '3 business days' }),
      node('Report', 'document', 'Approval recommendation', { Template: 'Your supplier approval template', Includes: 'Findings, evidence, open items', Format: 'Document for review' }),
      node('Monitor', 'schedule', 'Quarterly vendor monitoring', { Frequency: 'Quarterly', Watch: 'Document expiry, financial changes, incidents', Alert: 'Supplier owner' }),
    ],
    preview: {
      title: 'Vendor dashboard',
      subtitle: 'Supplier onboarding · Q3',
      kpis: [
        { label: 'Onboarding', value: '14' },
        { label: 'Awaiting documents', value: '4' },
        { label: 'Approved', value: '31' },
      ],
      columns: ['Supplier', 'Category', 'Risk', 'Status'],
      rows: [
        ['Ardent Logistics', 'Freight', '22', 'Approved'],
        ['Kestrel Cloud', 'SaaS', '48', 'Security doc requested'],
        ['Mira Components', 'Manufacturing', '74', 'Escalated'],
        ['Quarry Analytics', 'Data', '35', 'In review'],
      ],
    },
    dataSources: ['Supplier portal form', 'Uploaded supplier documents', 'Company registry data', 'Financial data feed', 'Your ERP supplier list'],
    governance: ['Risk score ≥ 70 escalates to risk committee', 'Critical suppliers need two approvers', 'Every finding links to its source document', 'Approval decisions are recorded with the approver and date'],
  },
  {
    id: 'vc',
    name: 'Venture Capital Workspace',
    prompt: 'Build an investment committee workflow for our venture fund, from inbound pitch decks to IC memo and portfolio monitoring.',
    reply: 'Starting from the DGPT venture capital workspace. I’ve set up sourcing, thesis qualification, IC and portfolio monitoring.',
    plan: ['Pitch deck and application intake', 'Thesis fit scoring against your criteria', 'Research and gap analysis', 'Partner review rule and IC approval', 'IC memo from your template', 'Quarterly portfolio monitoring'],
    nodes: [
      node('Intake', 'intake', 'Pitch decks and applications', { Channel: 'Application form, forwarded email', Accepts: 'Pitch deck, company details', Owner: 'Analyst team' }),
      node('Extract', 'agent', 'Deck Extraction Agent', { Agent: 'Document Extraction', Output: 'Company profile, metrics, round details' }),
      node('Thesis fit', 'agent', 'Thesis Fit Agent', { Agent: 'Thesis Fit', Criteria: 'Your fund thesis', Output: 'Fit score with reasons' }),
      node('Enrich', 'data', 'Company and market data', { Source: 'Public company and market data', Status: 'Connection configured at deployment' }),
      node('Analyze', 'agent', 'Research Agent', { Agent: 'Company Research', Output: 'Market, competitors, open questions' }),
      node('Fit threshold', 'condition', 'If fit score ≥ 70', { Rule: 'Fit score ≥ 70', 'If true': 'Partner review', Otherwise: 'Polite pass queue', Type: 'Deterministic rule' }),
      node('IC approval', 'human', 'Investment committee', { Approver: 'Investment committee', Quorum: '3 partners' }),
      node('IC memo', 'document', 'IC memo', { Template: 'Your IC memo template', Includes: 'Thesis fit, risks, open questions' }),
      node('Monitor', 'schedule', 'Portfolio monitoring', { Frequency: 'Quarterly', Watch: 'KPIs, runway, follow-on signals' }),
    ],
    preview: {
      title: 'Deal pipeline',
      subtitle: 'Fund III · active deals',
      kpis: [
        { label: 'In pipeline', value: '48' },
        { label: 'In diligence', value: '9' },
        { label: 'At IC', value: '3' },
      ],
      columns: ['Company', 'Stage', 'Fit', 'Status'],
      rows: [
        ['Northwind Robotics', 'Seed', '82', 'Partner review'],
        ['Halcyon Grid', 'Series A', '76', 'Diligence'],
        ['Corvid Labs', 'Seed', '64', 'Info requested'],
        ['Parallax Health', 'Pre-seed', '58', 'Below threshold'],
      ],
    },
    dataSources: ['Application form', 'Forwarded deal emails', 'Pitch decks and data rooms', 'Public company data', 'Past IC memos'],
    governance: ['Fit score ≥ 70 routes to partner review', 'IC decisions need a 3-partner quorum', 'Memos cite the source of each claim'],
  },
  {
    id: 'pe',
    name: 'Private Equity Diligence',
    prompt: 'Create a private equity diligence process covering target assessment, financial diligence, commercial evaluation and portfolio analysis.',
    reply: 'Here’s a private equity diligence process. I’ve added financial and commercial workstreams and an IC screen.',
    plan: ['Data room intake', 'Financial diligence and benchmarking', 'Commercial evaluation', 'Leverage rule and IC approval', 'IC paper', 'Monthly portfolio KPI pack'],
    nodes: [
      node('Intake', 'intake', 'Target data room', { Channel: 'Data room', Owner: 'Deal team' }),
      node('Extract', 'agent', 'Document Extraction Agent', { Agent: 'Document Extraction', Output: 'Financials, contracts, management info' }),
      node('Financial diligence', 'agent', 'Financial Analysis Agent', { Agent: 'Financial Analysis', Output: 'Earnings quality, working capital, debt' }),
      node('Commercial', 'agent', 'Market Intelligence Agent', { Agent: 'Market Intelligence', Output: 'Market position and customer concentration' }),
      node('Benchmark', 'agent', 'Benchmarking Agent', { Agent: 'Benchmarking', Compare: 'Sector peers' }),
      node('Leverage check', 'condition', 'If leverage > 5.0x', { Rule: 'Net debt / EBITDA > 5.0x', 'If true': 'Escalate to credit review', Otherwise: 'Continue to IC', Type: 'Deterministic rule' }),
      node('Approve', 'human', 'Investment committee', { Approver: 'Investment committee' }),
      node('Report', 'document', 'IC paper', { Template: 'Your IC paper template' }),
      node('Monitor', 'schedule', 'Portfolio KPI pack', { Frequency: 'Monthly', Watch: 'Revenue, EBITDA, covenants' }),
    ],
    preview: {
      title: 'Investment committee screen',
      subtitle: 'Targets in review',
      kpis: [
        { label: 'Targets', value: '6' },
        { label: 'Open workstreams', value: '14' },
        { label: 'IC this month', value: '2' },
      ],
      columns: ['Target', 'Sector', 'EV / EBITDA', 'Status'],
      rows: [
        ['Brightline Services', 'Facilities', '8.4x', 'IC scheduled'],
        ['Oakmere Software', 'Vertical SaaS', '14.1x', 'Financial review'],
        ['Tessel Foods', 'Consumer', '9.2x', 'Credit review'],
      ],
    },
    dataSources: ['Target data room', 'Management accounts', 'Sector benchmarks', 'Portfolio reporting'],
    governance: ['Leverage above 5.0x escalates to credit review', 'IC approval required before LOI', 'All figures trace to data room documents'],
  },
  {
    id: 'ma',
    name: 'M&A Evaluation',
    prompt: 'Design an M&A evaluation application with target screening, data room analysis, contractual review and integration risk evaluation.',
    reply: 'Here’s an M&A evaluation application. Each workstream has an owner, and contract risks route to legal review.',
    plan: ['Target screening', 'Data room analysis', 'Contract review', 'Change-of-control rule', 'Deal committee approval', 'Acquisition brief and integration milestones'],
    nodes: [
      node('Intake', 'intake', 'Target data room', { Channel: 'Data room', Owner: 'Deal lead' }),
      node('Extract', 'agent', 'Document Extraction Agent', { Agent: 'Document Extraction', Output: 'Indexed data room' }),
      node('Contract review', 'agent', 'Contract Review Agent', { Agent: 'Contract Review', Output: 'Key terms, obligations, change-of-control clauses' }),
      node('Enrich', 'data', 'Registry and market data', { Source: 'Public registries', Status: 'Connection configured at deployment' }),
      node('Workstreams', 'agent', 'Risk Identification Agent', { Agent: 'Risk Identification', Covers: 'Commercial, financial, technical, organizational' }),
      node('Clause check', 'condition', 'If change-of-control found', { Rule: 'Change-of-control clause present', 'If true': 'Legal review', Otherwise: 'Continue', Type: 'Deterministic rule' }),
      node('Approve', 'human', 'Deal committee', { Approver: 'Deal committee' }),
      node('Brief', 'document', 'Acquisition brief', { Template: 'Your acquisition brief' }),
      node('Monitor', 'schedule', 'Integration milestones', { Frequency: 'Monthly for 12 months' }),
    ],
    preview: {
      title: 'Risk analysis report',
      subtitle: 'Project Larch · pre-signing',
      kpis: [
        { label: 'Workstreams', value: '5' },
        { label: 'Open issues', value: '7' },
        { label: 'Need resolution', value: '2' },
      ],
      columns: ['Workstream', 'Owner', 'Finding', 'Status'],
      rows: [
        ['Legal', 'J. Okafor', 'Change-of-control in 3 contracts', 'Legal review'],
        ['Financial', 'R. Patel', 'Deferred revenue restated', 'Resolved'],
        ['Technical', 'S. Lind', 'Licence audit pending', 'Open'],
      ],
    },
    dataSources: ['Target data room', 'Contract repository', 'Public registries', 'Reviewer notes'],
    governance: ['Change-of-control clauses route to legal review', 'Deal committee signs off before signing', 'Each finding has a named owner'],
  },
  {
    id: 'grants',
    name: 'Grant Review System',
    prompt: 'Build a grant review system with application intake, eligibility scoring, reviewer assignments and funding reports.',
    reply: 'Here’s a grant review system. Eligibility runs first, then reviewers score against your shared rubric.',
    plan: ['Application portal', 'Eligibility check', 'Rubric scoring', 'Reviewer assignment', 'Panel approval', 'Funding report and milestone tracking'],
    nodes: [
      node('Intake', 'intake', 'Application portal', { Channel: 'Web application form', Owner: 'Program team' }),
      node('Eligibility', 'agent', 'Eligibility Agent', { Agent: 'Eligibility', Rules: 'Your program criteria' }),
      node('Score', 'agent', 'Fit Scoring Agent', { Agent: 'Fit Scoring', Rubric: 'Shared program rubric', Output: 'Score per criterion with evidence' }),
      node('Assign reviewers', 'human', 'Review assignments', { Assign: '2 reviewers per application', Conflicts: 'Declared before review' }),
      node('Shortlist rule', 'condition', 'If score ≥ 3.5', { Rule: 'Average score ≥ 3.5 / 5', 'If true': 'Panel review', Otherwise: 'Decline with feedback', Type: 'Deterministic rule' }),
      node('Panel', 'human', 'Selection panel', { Approver: 'Selection panel' }),
      node('Report', 'document', 'Funding report', { Template: 'Your funding report' }),
      node('Monitor', 'schedule', 'Milestone reports', { Frequency: 'Per grant milestone' }),
    ],
    preview: {
      title: 'Grant application portal',
      subtitle: 'Innovation Fund · Round 4',
      kpis: [
        { label: 'Applications', value: '212' },
        { label: 'Eligible', value: '164' },
        { label: 'Shortlisted', value: '22' },
      ],
      columns: ['Applicant', 'Program', 'Score', 'Status'],
      rows: [
        ['Fernhill Materials', 'Climate', '4.4', 'Shortlisted'],
        ['Juniper Diagnostics', 'Health', '3.9', 'Panel review'],
        ['Ostrava Labs', 'Climate', '3.1', 'Declined'],
      ],
    },
    dataSources: ['Application portal', 'Supporting documents', 'Reviewer scores'],
    governance: ['Eligibility rules run before scoring', 'Reviewers declare conflicts', 'Every shortlist decision records a reason'],
  },
  {
    id: 'compliance',
    name: 'Compliance Assessment',
    prompt: 'Create a custom compliance assessment for prospective partners using our internal policies.',
    reply: 'Here’s a compliance assessment built around your internal policies. Failed controls route to remediation before approval.',
    plan: ['Questionnaire intake', 'Policy assessment', 'Evidence verification', 'Failed-control rule', 'Risk owner approval', 'Assessment report and document expiry monitoring'],
    nodes: [
      node('Intake', 'intake', 'Partner questionnaire', { Channel: 'Questionnaire and uploads', Owner: 'Risk team' }),
      node('Extract', 'agent', 'Document Extraction Agent', { Agent: 'Document Extraction' }),
      node('Policy assessment', 'agent', 'Policy Assessment Agent', { Agent: 'Policy Assessment', Policies: 'Your internal policy set' }),
      node('Verify evidence', 'agent', 'Verification Agent', { Agent: 'Verification', Output: 'Verified vs outstanding evidence' }),
      node('Control check', 'condition', 'If any control fails', { Rule: 'Any required control failed', 'If true': 'Remediation request', Otherwise: 'Continue to approval', Type: 'Deterministic rule' }),
      node('Approve', 'human', 'Risk owner', { Approver: 'Risk owner' }),
      node('Report', 'document', 'Assessment report', { Template: 'Your assessment template' }),
      node('Monitor', 'schedule', 'Document expiry', { Watch: 'Certificates and policy documents', Alert: '30 days before expiry' }),
    ],
    preview: {
      title: 'Executive decision dashboard',
      subtitle: 'Partner assessments',
      kpis: [
        { label: 'Assessments', value: '18' },
        { label: 'Controls passing', value: '86%' },
        { label: 'Awaiting evidence', value: '5' },
      ],
      columns: ['Entity', 'Policy set', 'Result', 'Status'],
      rows: [
        ['Halden Partners', 'Internal v3', 'Pass', 'Approved'],
        ['Marrow & Co', 'Internal v3', '2 open', 'Evidence requested'],
        ['Peregrine Data', 'Internal v3', '1 failed', 'Remediation'],
      ],
    },
    dataSources: ['Partner questionnaire', 'Uploaded policies and certificates', 'Your internal policy library'],
    governance: ['Failed controls block approval', 'Verified and outstanding evidence kept apart', 'Approvals recorded with policy version'],
  },
  {
    id: 'blank',
    name: 'Blank Workspace',
    prompt: 'Start from a blank workspace.',
    reply: 'Blank workspace ready. Describe your process, pick a template, or add agents from the Agent Library.',
    plan: [],
    nodes: [],
    preview: { title: 'Your application', subtitle: 'Nothing built yet', kpis: [], columns: [], rows: [] },
    dataSources: [],
    governance: [],
  },
]

export const getBuilderTemplate = (id: BuilderTemplateId) => BUILDER_TEMPLATES.find((t) => t.id === id) ?? BUILDER_TEMPLATES[0]

export const isBuilderTemplateId = (value: string | null): value is BuilderTemplateId => BUILDER_TEMPLATES.some((t) => t.id === value)

/** Matches a free-text prompt to a scripted template (preview only). */
export function matchBuilderTemplate(text: string): BuilderTemplateId {
  const t = text.toLowerCase()
  if (/procure|supplier|vendor|rfp/.test(t)) return 'procurement'
  if (/grant|funding program|applicant/.test(t)) return 'grants'
  if (/private equity|\bpe\b|buyout/.test(t)) return 'pe'
  if (/m&a|merger|acqui/.test(t)) return 'ma'
  if (/complian|policy|regulat|control/.test(t)) return 'compliance'
  if (/invest|venture|\bvc\b|committee|deal|portfolio/.test(t)) return 'vc'
  return 'blank'
}
